/**
 * Template Store
 *
 * Zustand store for managing HVAC template state including:
 * - Template library browsing and search
 * - Wizard state for template application
 * - Zone selections
 * - Parameter configuration
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  HVACTemplate,
  TemplateCategory,
  TemplateWizardState,
  WizardStep,
  ZoneSelection,
  GeneratedSystem,
  TemplateApplicationResult,
  PlantLoopTemplate,
} from './types';
import { HVAC_TEMPLATES, getTemplateById } from './templates';
import { WIZARD_STEPS } from './types';

interface TemplateState {
  /** All available templates */
  templates: HVACTemplate[];

  /** Search query for filtering templates */
  searchQuery: string;

  /** Selected category filter (null = all) */
  selectedCategory: TemplateCategory | null;

  /** Wizard state */
  wizard: TemplateWizardState;

  /** Whether the template panel is open */
  isPanelOpen: boolean;

  /** Available zones from IDF (populated when wizard opens) */
  availableZones: Array<{ name: string; id: string }>;

  /** Generated system preview (computed before applying) */
  previewSystem: GeneratedSystem | null;

  /** Last application result */
  lastResult: TemplateApplicationResult | null;

  // Template Library Actions

  /** Set search query */
  setSearchQuery: (query: string) => void;

  /** Set category filter */
  setSelectedCategory: (category: TemplateCategory | null) => void;

  /** Get filtered templates */
  getFilteredTemplates: () => HVACTemplate[];

  /** Open template panel */
  openPanel: () => void;

  /** Close template panel */
  closePanel: () => void;

  // Wizard Actions

  /** Start wizard with a template */
  startWizard: (templateId: string) => void;

  /** Go to next wizard step */
  nextStep: () => void;

  /** Go to previous wizard step */
  previousStep: () => void;

  /** Go to specific step */
  goToStep: (step: WizardStep) => void;

  /** Update parameter value */
  setParameterValue: (parameterId: string, value: string | number | boolean) => void;

  /** Set all parameter values at once */
  setParameterValues: (values: Record<string, string | number | boolean>) => void;

  /** Set available zones */
  setAvailableZones: (zones: Array<{ name: string; id: string }>) => void;

  /** Toggle zone selection */
  toggleZoneSelection: (zoneId: string) => void;

  /** Select all zones */
  selectAllZones: () => void;

  /** Deselect all zones */
  deselectAllZones: () => void;

  /** Update zone terminal type */
  setZoneTerminalType: (zoneId: string, terminalType: string) => void;

  /** Update plant loop configuration */
  setPlantLoopConfig: (loopType: string, config: Partial<PlantLoopTemplate>) => void;

  /** Validate current wizard state */
  validateWizard: () => string[];

  /** Check if current step is complete */
  isStepComplete: (step: WizardStep) => boolean;

  /** Get current step index */
  getCurrentStepIndex: () => number;

  /** Get total steps count */
  getTotalSteps: () => number;

  /** Set preview system */
  setPreviewSystem: (system: GeneratedSystem | null) => void;

  /** Set last application result */
  setLastResult: (result: TemplateApplicationResult | null) => void;

  /** Reset wizard to initial state */
  resetWizard: () => void;

  /** Cancel wizard and close panel */
  cancelWizard: () => void;

  /** Get selected template */
  getSelectedTemplate: () => HVACTemplate | null;

  /** Get selected zones */
  getSelectedZones: () => ZoneSelection[];

  /** Check if can proceed to next step */
  canProceed: () => boolean;
}

/**
 * Initial wizard state
 */
const initialWizardState: TemplateWizardState = {
  currentStep: 'select-template',
  selectedTemplate: null,
  parameterValues: {},
  zoneSelections: [],
  plantLoopConfigs: {},
  errors: [],
  isComplete: false,
};

/**
 * Get default parameter values from template
 */
function getDefaultParameterValues(template: HVACTemplate): Record<string, string | number | boolean> {
  const values: Record<string, string | number | boolean> = {};
  for (const param of template.parameters) {
    values[param.id] = param.defaultValue;
  }
  return values;
}

/**
 * Get default plant loop configs from template
 */
function getDefaultPlantLoopConfigs(template: HVACTemplate): Record<string, Partial<PlantLoopTemplate>> {
  const configs: Record<string, Partial<PlantLoopTemplate>> = {};
  if (template.plantLoops) {
    for (const loop of template.plantLoops) {
      configs[loop.type] = { ...loop };
    }
  }
  return configs;
}

export const useTemplateStore = create<TemplateState>()(
  devtools(
    (set, get) => ({
      templates: HVAC_TEMPLATES,
      searchQuery: '',
      selectedCategory: null,
      wizard: initialWizardState,
      isPanelOpen: false,
      availableZones: [],
      previewSystem: null,
      lastResult: null,

      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      setSelectedCategory: (category: TemplateCategory | null) => {
        set({ selectedCategory: category });
      },

      getFilteredTemplates: () => {
        const { templates, searchQuery, selectedCategory } = get();
        let filtered = templates;

        // Filter by category
        if (selectedCategory) {
          filtered = filtered.filter((t) => t.category === selectedCategory);
        }

        // Filter by search query
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          filtered = filtered.filter(
            (t) =>
              t.name.toLowerCase().includes(query) ||
              t.description.toLowerCase().includes(query)
          );
        }

        return filtered;
      },

      openPanel: () => {
        set({ isPanelOpen: true });
      },

      closePanel: () => {
        set({ isPanelOpen: false });
      },

      startWizard: (templateId: string) => {
        const template = getTemplateById(templateId);
        if (!template) {
          console.error(`Template not found: ${templateId}`);
          return;
        }

        const defaultValues = getDefaultParameterValues(template);
        const plantLoopConfigs = getDefaultPlantLoopConfigs(template);

        set({
          wizard: {
            currentStep: 'configure-system',
            selectedTemplate: template,
            parameterValues: defaultValues,
            zoneSelections: [],
            plantLoopConfigs,
            errors: [],
            isComplete: false,
          },
        });
      },

      nextStep: () => {
        const { wizard } = get();
        const currentIndex = WIZARD_STEPS.findIndex((s) => s.id === wizard.currentStep);

        if (currentIndex < WIZARD_STEPS.length - 1) {
          const nextStep = WIZARD_STEPS[currentIndex + 1];
          if (nextStep) {
            set({
              wizard: {
                ...wizard,
                currentStep: nextStep.id,
              },
            });
          }
        }
      },

      previousStep: () => {
        const { wizard } = get();
        const currentIndex = WIZARD_STEPS.findIndex((s) => s.id === wizard.currentStep);

        if (currentIndex > 0) {
          const prevStep = WIZARD_STEPS[currentIndex - 1];
          if (prevStep) {
            set({
              wizard: {
                ...wizard,
                currentStep: prevStep.id,
              },
            });
          }
        }
      },

      goToStep: (step: WizardStep) => {
        const { wizard } = get();
        set({
          wizard: {
            ...wizard,
            currentStep: step,
          },
        });
      },

      setParameterValue: (parameterId: string, value: string | number | boolean) => {
        const { wizard } = get();
        set({
          wizard: {
            ...wizard,
            parameterValues: {
              ...wizard.parameterValues,
              [parameterId]: value,
            },
          },
        });
      },

      setParameterValues: (values: Record<string, string | number | boolean>) => {
        const { wizard } = get();
        set({
          wizard: {
            ...wizard,
            parameterValues: {
              ...wizard.parameterValues,
              ...values,
            },
          },
        });
      },

      setAvailableZones: (zones: Array<{ name: string; id: string }>) => {
        const { wizard } = get();
        const defaultTerminalType = wizard.selectedTemplate?.terminalTypes?.[0]?.componentType;
        // Create zone selections from available zones
        const zoneSelections: ZoneSelection[] = zones.map((z) => {
          const selection: ZoneSelection = {
            zoneName: z.name,
            zoneId: z.id,
            selected: false,
          };
          if (defaultTerminalType) {
            selection.terminalType = defaultTerminalType;
          }
          return selection;
        });

        set({
          availableZones: zones,
          wizard: {
            ...wizard,
            zoneSelections,
          },
        });
      },

      toggleZoneSelection: (zoneId: string) => {
        const { wizard } = get();
        const zoneSelections = wizard.zoneSelections.map((z) =>
          z.zoneId === zoneId ? { ...z, selected: !z.selected } : z
        );
        set({
          wizard: {
            ...wizard,
            zoneSelections,
          },
        });
      },

      selectAllZones: () => {
        const { wizard } = get();
        const zoneSelections = wizard.zoneSelections.map((z) => ({
          ...z,
          selected: true,
        }));
        set({
          wizard: {
            ...wizard,
            zoneSelections,
          },
        });
      },

      deselectAllZones: () => {
        const { wizard } = get();
        const zoneSelections = wizard.zoneSelections.map((z) => ({
          ...z,
          selected: false,
        }));
        set({
          wizard: {
            ...wizard,
            zoneSelections,
          },
        });
      },

      setZoneTerminalType: (zoneId: string, terminalType: string) => {
        const { wizard } = get();
        const zoneSelections = wizard.zoneSelections.map((z) =>
          z.zoneId === zoneId ? { ...z, terminalType } : z
        );
        set({
          wizard: {
            ...wizard,
            zoneSelections,
          },
        });
      },

      setPlantLoopConfig: (loopType: string, config: Partial<PlantLoopTemplate>) => {
        const { wizard } = get();
        set({
          wizard: {
            ...wizard,
            plantLoopConfigs: {
              ...wizard.plantLoopConfigs,
              [loopType]: {
                ...wizard.plantLoopConfigs[loopType],
                ...config,
              },
            },
          },
        });
      },

      validateWizard: (): string[] => {
        const { wizard } = get();
        const errors: string[] = [];

        if (!wizard.selectedTemplate) {
          errors.push('No template selected');
          return errors;
        }

        const template = wizard.selectedTemplate;

        // Validate parameters
        for (const param of template.parameters) {
          const value = wizard.parameterValues[param.id];

          if (param.validation?.required && (value === undefined || value === '')) {
            errors.push(`${param.name} is required`);
          }

          if (param.type === 'number' && typeof value === 'number') {
            if (param.validation?.min !== undefined && value < param.validation.min) {
              errors.push(`${param.name} must be at least ${String(param.validation.min)}`);
            }
            if (param.validation?.max !== undefined && value > param.validation.max) {
              errors.push(`${param.name} must be at most ${String(param.validation.max)}`);
            }
          }
        }

        // Validate zone selections
        if (template.requiresZones) {
          const selectedZones = wizard.zoneSelections.filter((z) => z.selected);

          if (template.minZones && selectedZones.length < template.minZones) {
            errors.push(`At least ${String(template.minZones)} zone(s) must be selected`);
          }

          if (template.maxZones && selectedZones.length > template.maxZones) {
            errors.push(`At most ${String(template.maxZones)} zone(s) can be selected`);
          }
        }

        set({
          wizard: {
            ...wizard,
            errors,
          },
        });

        return errors;
      },

      isStepComplete: (step: WizardStep): boolean => {
        const { wizard } = get();

        switch (step) {
          case 'select-template':
            return wizard.selectedTemplate !== null;

          case 'configure-system':
            // Check required parameters have values
            if (!wizard.selectedTemplate) return false;
            for (const param of wizard.selectedTemplate.parameters) {
              if (param.validation?.required) {
                const value = wizard.parameterValues[param.id];
                if (value === undefined || value === '') return false;
              }
            }
            return true;

          case 'assign-zones':
            if (!wizard.selectedTemplate) return false;
            if (!wizard.selectedTemplate.requiresZones) return true;
            const selectedCount = wizard.zoneSelections.filter((z) => z.selected).length;
            if (wizard.selectedTemplate.minZones && selectedCount < wizard.selectedTemplate.minZones) {
              return false;
            }
            return selectedCount > 0;

          case 'plant-loops':
            // Plant loops step is optional - always complete
            return true;

          case 'review':
            // Review step requires all previous steps to be complete
            return (
              get().isStepComplete('select-template') &&
              get().isStepComplete('configure-system') &&
              get().isStepComplete('assign-zones')
            );

          default:
            return false;
        }
      },

      getCurrentStepIndex: (): number => {
        const { wizard } = get();
        return WIZARD_STEPS.findIndex((s) => s.id === wizard.currentStep);
      },

      getTotalSteps: (): number => {
        return WIZARD_STEPS.length;
      },

      setPreviewSystem: (system: GeneratedSystem | null) => {
        set({ previewSystem: system });
      },

      setLastResult: (result: TemplateApplicationResult | null) => {
        set({ lastResult: result });
      },

      resetWizard: () => {
        set({
          wizard: initialWizardState,
          previewSystem: null,
          lastResult: null,
        });
      },

      cancelWizard: () => {
        set({
          wizard: initialWizardState,
          isPanelOpen: false,
          previewSystem: null,
        });
      },

      getSelectedTemplate: (): HVACTemplate | null => {
        return get().wizard.selectedTemplate;
      },

      getSelectedZones: (): ZoneSelection[] => {
        return get().wizard.zoneSelections.filter((z) => z.selected);
      },

      canProceed: (): boolean => {
        const { wizard } = get();
        return get().isStepComplete(wizard.currentStep);
      },
    }),
    { name: 'TemplateStore' }
  )
);

/**
 * Selector for filtered templates
 */
export const useFilteredTemplates = () => {
  return useTemplateStore((state) => state.getFilteredTemplates());
};

/**
 * Selector for wizard state
 */
export const useWizardState = () => {
  return useTemplateStore((state) => state.wizard);
};

/**
 * Selector for selected template
 */
export const useSelectedTemplate = () => {
  return useTemplateStore((state) => state.wizard.selectedTemplate);
};

/**
 * Selector for current step
 */
export const useCurrentStep = () => {
  return useTemplateStore((state) => state.wizard.currentStep);
};

/**
 * Selector for parameter values
 */
export const useParameterValues = () => {
  return useTemplateStore((state) => state.wizard.parameterValues);
};

/**
 * Selector for zone selections
 */
export const useZoneSelections = () => {
  return useTemplateStore((state) => state.wizard.zoneSelections);
};
